<template>
  <div class="modals">
    <modal-login v-if="loginModal.active" @close="hideLoginModal" />
    <modal-error v-if="errorModal.active" @close="hideErrorModal" />

    <modal
      v-if="simpleModal.active && simpleModal.type === 'STANDARD'"
      :id="simpleModal.id"
      :heading="simpleModal.props.heading"
      :subheading="simpleModal.props.subheading"
      :modal-class="simpleModal.props.modalClass || '-confirm-remove'"
      :show-footer="simpleModal.props.showFooter"
      @close="closeSimpleModal"
    >
      <p v-if="simpleModal.props.bodySlot" class="light">{{ simpleModal.props.bodySlot }}</p>
    </modal>

    <modal-confirm
      v-if="simpleModal.active && simpleModal.type === 'CONFIRM'"
      :id="simpleModal.id"
      :heading="simpleModal.props.heading"
      :subheading="simpleModal.props.subheading"
      :button-text="simpleModal.props.buttonText"
      :cancel-text="simpleModal.props.cancelText"
      :url="simpleModal.props.url"
      @confirm="closeSimpleModalWithAction('confirm')"
      @close="closeSimpleModalWithAction('close')"
    />

    <modal-calendar
      v-if="simpleModal.active && simpleModal.type === 'CALENDAR_ADD'"
      :id="simpleModal.id"
      :entity-id="simpleModal.props.entityId"
      :entity-type="simpleModal.props.entityType"
      :entity-title="simpleModal.props.entityTitle"
      :entity-category="simpleModal.props.entityCategory"
      @schedule-success="setRefreshScheduledInstances(true)"
      @close="closeSimpleModal"
    />

    <modal-routine
      v-if="simpleModal.active && simpleModal.type === 'ROUTINE_ADD'"
      :id="simpleModal.id"
      :entity-id="simpleModal.props.entityId"
      :entity-type="simpleModal.props.entityType"
      :entity-title="simpleModal.props.entityTitle"
      @close="closeSimpleModal"
    />

    <modal-flag
      v-if="simpleModal.active && simpleModal.type === 'FLAG'"
      :id="simpleModal.id"
      :flaggable-id="simpleModal.props.flaggableId"
      :flaggable-type="simpleModal.props.flaggableType"
      @close="closeFlagModal"
      @flag-updated="setSimpleModalAction('flagged')"
    />

    <modal-similar
      v-if="simpleModal.active && simpleModal.type === 'SIMILAR'"
      :id="simpleModal.id"
      :entity-id="simpleModal.props.entityId"
      :entity-type="simpleModal.props.entityType"
      :entity-title="simpleModal.props.entityTitle"
      @close="closeSimpleModal"
    />

    <modal-tags
      v-if="simpleModal.active && simpleModal.type === 'TAGS'"
      :id="simpleModal.id"
      :entity-id="simpleModal.props.entityId"
      :entity-type="simpleModal.props.entityType"
      :entity-title="simpleModal.props.entityTitle"
      :entity-category="simpleModal.props.entityCategory"
      @close="closeSimpleModal"
    />

    {{/* modal powered by plan-card-add-to-cart-button and plan-detail-add-to-cart-button emit */}}
    <modal-plan-purchase
      v-if="simpleModal.active && simpleModal.type === 'PLAN_PURCHASE'"
      :id="simpleModal.id"
      :plan="simpleModal.props.plan"
      :plus-pass="simpleModal.props.plusPass"
      :image-url-prefix="simpleModal.props.imageUrlPrefix"
      @close="closeSimpleModal"
    />

    <modal-plus-pass-purchase
      v-if="simpleModal.active && simpleModal.type === 'PASS_PURCHASE'"
      :id="simpleModal.id"
      :plus-pass="simpleModal.props.plusPass"
      :cheapest-monthly-price="simpleModal.props.cheapestMonthlyPrice"
      :image-url-prefix="simpleModal.props.imageUrlPrefix"
      :image="simpleModal.props.image"
      :entity-id="simpleModal.props.entityId"
      :entity-type="simpleModal.props.entityType"
      :entity-category="simpleModal.props.entityCategory"
      @close="closeSimpleModal"
    />

    <modal-plan-copy
      v-if="simpleModal.active && simpleModal.type === 'PLAN_COPY'"
      :id="simpleModal.id"
      :plan-id="simpleModal.props.planId"
      :grouping-id="simpleModal.props.groupingId"
      :plan-short-name="simpleModal.props.planShortName"
      :copy-type="simpleModal.props.copyType"
      @close="closeSimpleModal"
    />

    {{/* powered by separate pinia modal */}}
    <complete-sliders v-if="user.isLoggedIn" />

    {{/* guided tours */}}
    <guided-tours v-if="user.isLoggedIn" />
  </div>
</template>

<script>
// @flow
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useCalendarStore } from '@/stores/calendar'
import ModalError from '@/components/modals/modal-error.vue'
import ModalLogin from '@/components/modals/modal-login.vue'
import ModalCalendar from '@/components/modals/modal-calendar.vue'
import ModalRoutine from '@/components/modals/modal-routine.vue'
import Modal from '@/components/modals/modal.vue'
import ModalConfirm from '@/components/modals/modal-confirm.vue'
import ModalFlag from '@/components/modals/modal-flag.vue'
import ModalSimilar from '@/components/modals/modal-similar.vue'
import ModalTags from '@/components/modals/modal-tags.vue'
import CompleteSliders from '@/components/modals/sliders/complete-sliders.vue'
import ModalPlanPurchase from '@/components/modals/modal-plan-purchase.vue'
import ModalPlusPassPurchase from '@/components/modals/modal-plus-pass-purchase.vue'
import ModalPlanCopy from '@/components/modals/modal-plan-copy.vue'
import GuidedTours from '@/components/tours/guided-tours.vue'

export default {
  components: {
    GuidedTours,
    ModalError,
    ModalLogin,
    ModalCalendar,
    CompleteSliders,
    ModalRoutine,
    ModalConfirm,
    Modal,
    ModalFlag,
    ModalSimilar,
    ModalTags,
    ModalPlanPurchase,
    ModalPlusPassPurchase,
    ModalPlanCopy,
  },
  computed: {
    ...mapState(useUserAuthStore, ['user']),
    ...mapState(useModalsStore, ['loginModal', 'errorModal', 'simpleModal']),
  },

  methods: {
    ...mapActions(useModalsStore, [
      'hideErrorModal',
      'hideLoginModal',
      'closeSimpleModal',
      'closeSimpleModalWithAction',
      'setSimpleModalAction',
    ]),
    ...mapActions(useCalendarStore, ['setRefreshScheduledInstances']),

    // has thank you screen, so close with action
    // if flag-updated was emitted w/ action
    closeFlagModal() {
      if (this.simpleModal.action) {
        this.closeSimpleModalWithAction(this.simpleModal.action)
        return
      }

      this.closeSimpleModal()
    },
  },
}
</script>
